import i18n from "@/i18n";
import { API_ERROR_CODES, ApiCallError } from "@/api";

/**
 * What is said when a department has no seat for somebody — the one sentence the Operator's page
 * ("every seat is in use") and every refused write (a toast, a dialog's banner) put the same way, so
 * the pure part is held here and tested without a component.
 *
 * The server decides: a person needs a seat to ACT in a department's dataspace, there are as many
 * floating seats as the department's license says, a named seat is the person's own, and one that is
 * let go of frees up two minutes later (`server/src/seats/`). A refusal arrives as a
 * `403 seat_unavailable` over REST, with `details`, or as the ack of a `seat:claim` over the socket;
 * this only words both.
 */

/** A refusal for want of a seat, as much of it as the server said: all of it optional. */
export interface SeatRefusal {
	/** The department's name — the socket's answer names only its id, so the page supplies the name. */
	department: string | null;
	/** How many floating seats the department has. */
	limit: number | null;
}

/**
 * The refusal a `403 seat_unavailable` carries, or `null` for any other error. A refusal whose
 * `details` are missing or not what this client expects is still a refusal for want of a seat,
 * said without the numbers rather than not at all.
 */
export function seatUnavailableFromError(error: unknown): SeatRefusal | null {
	if (!(error instanceof ApiCallError) || error.code !== API_ERROR_CODES.seatUnavailable)
		return null;
	const d = error.details as { department_name?: unknown; limit?: unknown } | null | undefined;
	return {
		department:
			typeof d?.department_name === "string" && d.department_name ? d.department_name : null,
		limit: typeof d?.limit === "number" && Number.isFinite(d.limit) ? d.limit : null,
	};
}

/**
 * "All 3 floating seats in Sales are in use." — with the department and the number when they are
 * known, and with "this department" and "every seat" when they are not. A department with one seat
 * and one with none say so in their own words, since "all 1" and "all 0" are not sentences.
 */
export function seatUnavailableSentence(refusal: SeatRefusal): string {
	const { department, limit } = refusal;
	const named = { department: department ?? "" };
	if (limit === null) {
		return i18n.global.t(`seats.unavailable.${department ? "anyNamed" : "any"}`, named);
	}
	return i18n.global.t(
		`seats.unavailable.${department ? "named" : "unnamed"}`,
		{ ...named, limit },
		limit,
	);
}

/**
 * What to do about it. A seat that is in use frees up when its holder leaves, so waiting helps; a
 * department with no floating seats at all has nothing to wait for, and the way out is to ask for one.
 */
export function seatUnavailableNote(refusal: SeatRefusal): string {
	return i18n.global.t(
		refusal.limit === 0 ? "seats.unavailable.noteNone" : "seats.unavailable.note",
	);
}

/** The sentence and what to do about it: for a toast or a banner that has no room for more. */
export function seatUnavailableMessage(refusal: SeatRefusal): string {
	return `${seatUnavailableSentence(refusal)} ${seatUnavailableNote(refusal)}`;
}
